import { z } from 'zod';
/**
 * WHICH TRANSPORT carries a notification. Channel-agnostic by design: the platform
 * notification service is reusable across every app in the suite, and email is only the
 * first channel to get an adapter (milestone User direction, 2026-08-13).
 *
 * `SLACK` and `TEAMS` (PRD-010) are CHAT channels: they never reach the email/SMS sender
 * factory — the notification module dispatches them through the `ChatSender` port. `TEAMS`
 * is APPENDED (a Postgres enum member's position is its ordinal). `channel` is chosen by
 * server code, never by a client.
 *
 * `PUSH` (S48-lh45, adr/0037) is the employee app's channel: dispatched through the
 * `PushSender` port by `service.ts#enqueuePushNotification`, never through this factory. APPENDED.
 *
 * Must stay identical — same members, SAME ORDER — to the `NotificationChannel` Prisma
 * enum (ADR-0011), proven by packages/db/src/notification-enums.test.ts.
 */
export const NOTIFICATION_CHANNELS = ['EMAIL', 'SLACK', 'SMS', 'TEAMS', 'PUSH'];
export const notificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);
/** WHAT happened that is worth telling someone about. One member in v1. */
export const NOTIFICATION_KINDS = [
    'VISITOR_ARRIVAL',
    // S41 — the Visit Pass. The first two go to the VISITOR (a raw address, not a `Person`);
    // the third goes to the host when their visitor finishes pre-registering.
    'VISITOR_INVITE',
    'VISITOR_INVITE_CANCELLED',
    'VISITOR_PREREGISTERED',
    // Handoff 0010 Phase D2 — billing notices to the tenant's OWNERS (raw addresses, not a
    // `Person`). A provider-backed trial converts to paid in a few days; an operator changed
    // what the tenant pays.
    'BILLING_TRIAL_ENDING',
    'BILLING_OPERATOR_PRICE_CHANGE',
    // Handoff 0013 §3 — collecting a subscription invoice failed; the owners are told once per
    // invoice so they can fix the payment method (or choose Free) before the provider gives up.
    'BILLING_PAYMENT_FAILED',
    // Handoff 0012 §5 (option B) — a pay-first change's bank payment cleared and the change is
    // active; and a payment was disputed (owners told; an operator decides, nothing is revoked).
    // A pay-first payment that FAILED reuses `BILLING_PAYMENT_FAILED`.
    'BILLING_CHANGE_ACTIVE',
    'BILLING_PAYMENT_DISPUTED',
    // S49-3u50 Phase 2b — the connected directory refused our grant; the tenant's OWNERS are told
    // once per break (raw addresses, not a `Person`) how to reconnect it.
    'DIRECTORY_SYNC_RECONNECT_REQUIRED',
    // Handoff 0017 — an arrival COPY: to a location's notification list (reception, security, a
    // shared inbox, §2) or to one of the host's assistants (§3). Its own kind, not
    // `VISITOR_ARRIVAL`, so the visit's deliveries tell "the host was told" apart from "a copy
    // went out", and nothing keyed on the host's arrival delivery ever counts a copy.
    'VISITOR_ARRIVAL_COPY',
    // Handoff 0017 §2 — a visitor checked out; sent to the location's notification list only
    // (entries with `onCheckout`). Hosts get no checkout alert.
    'VISITOR_CHECKOUT',
];
export const notificationKindSchema = z.enum(NOTIFICATION_KINDS);
/**
 * `PENDING` exists from day one because the row is written BEFORE the attempt and updated
 * after (milestone Constraint 3) — the producer/consumer shape S6's bus inherits without a
 * migration. `SKIPPED` is not decoration: "the host has no address" and (Phase 3) "the
 * tenant turned it off" are non-failures, and collapsing them into `FAILED` would train the
 * front desk to ignore `FAILED`.
 */
export const NOTIFICATION_DELIVERY_STATUSES = ['PENDING', 'SENT', 'FAILED', 'SKIPPED'];
export const notificationDeliveryStatusSchema = z.enum(NOTIFICATION_DELIVERY_STATUSES);
/**
 * What a READER of a delivery is allowed to see (docs/008 S7 — an allow-list, not a row).
 *
 * DELIBERATELY ABSENT, each for its own reason:
 *   `externalRef`   — a third-party internal id; S7 names those explicitly.
 *   `subjectType` / `subjectId` — the caller already knows what it asked about.
 *   `recipientPersonId` — the caller already knows who the host is.
 *   the address in any form other than `recipientHint`.
 */
export const notificationDeliveryDTOSchema = z.object({
    id: z.string().min(1),
    channel: notificationChannelSchema,
    kind: notificationKindSchema,
    status: notificationDeliveryStatusSchema,
    /** A MASKED address (`a***@northwind.test`), never a whole one. `'***'` when unknown. */
    recipientHint: z.string().min(1),
    /** snake_case machine reason, present only for FAILED and SKIPPED. */
    failureReason: z.string().nullable(),
    /** PRD-010 CHAT-12: the host-arrival email sent because chat could not reach the host. */
    chatFallback: z.boolean(),
    /**
     * PRD-010 CHAT-09: a copy of the arrival posted to a shared channel (`#reception`), not a
     * message to the host. `recipientHint` is then the channel's display name.
     */
    chatChannelPost: z.boolean(),
    enqueuedAt: z.string().datetime(),
    attemptedAt: z.string().datetime().nullable(),
});
/**
 * WHO SET A PERSON'S "DO NOT CONTACT" FLAG on a channel (issue #83). Must stay identical — same
 * members, SAME ORDER — to the `NotificationPreferenceSource` Prisma enum (ADR-0011), proven by
 * packages/db/src/notification-enums.test.ts.
 *
 * `ADMIN` — an administrator turned it on in the console; an administrator may clear it.
 * `SELF` — the person did it themselves; an administrator may not clear it on their behalf.
 * `CARRIER` — the recipient replied STOP to the carrier, learned from the send that tripped on
 * it. Nobody clears it from the console: re-texting someone who sent STOP is the violation.
 */
export const NOTIFICATION_PREFERENCE_SOURCES = ['ADMIN', 'SELF', 'CARRIER'];
export const notificationPreferenceSourceSchema = z.enum(NOTIFICATION_PREFERENCE_SOURCES);
/**
 * `GET`/`PUT /api/web/people/:id/sms-opt-out` — whether this person is texted at all.
 *
 * `source` and `optedOutAt` are `null` while the person has never been opted out. A cleared
 * `ADMIN` opt-out answers `optedOut: false` with `source: 'ADMIN'` (the last writer) and
 * `optedOutAt: null`. `clearable` is the server's answer to "may THIS caller turn it back on",
 * so a screen never re-derives the rule from `source`.
 */
export const personSmsOptOutDTOSchema = z.object({
    personId: z.string().min(1),
    optedOut: z.boolean(),
    source: notificationPreferenceSourceSchema.nullable(),
    optedOutAt: z.string().datetime().nullable(),
    clearable: z.boolean(),
});
/** `PUT /api/web/people/:id/sms-opt-out` body. Strict: the source is decided by the server. */
export const setPersonSmsOptOutSchema = z
    .object({
    optedOut: z.boolean(),
})
    .strict();
//# sourceMappingURL=notification.js.map